import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { Box, Button, Card, Grid, Icon, Link, Modal, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { helpers } from "~/lib/helpers";
import TopicFootprintSmall from "../TopicFootprintSmall";
import TopicSummaryRevenueCardModal from "../TopicSummaryRevenueCardModal";
const Scrollable = styled.div`
  overflow: auto;
  max-height: 600px;
  width: 100%;
`;
const ScrollableTable = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 300px;
  width: 100%;
  margin: 0 -8px 0 -8px;
`;
const StyledGrid = styled(Grid)`
  overflow: hidden;
  position: relative;
`;
const StyledCell = styled(Grid.Cell)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const GridRow = styled(Grid)`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr;
  gap: 8px;
  padding: 8px;
`;
const GridRowLink = styled(Link)`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.grey.secondary;
}};
  }
`;
const GridRowExisting = styled(Grid)`
  display: grid;
  grid-template-columns: 2.5fr 2.5fr 1fr 1fr;
  gap: 8px;
  padding: 8px;
`;
const GridRowLinkExisting = styled(Link)`
  display: grid;
  grid-template-columns: 2.5fr 2.5fr 1fr 1fr;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.grey.secondary;
}};
  }
`;
const ConcatText = styled(Text)``;
const StyledBox = styled(Box)`
  position: relative;
  border: 1px solid ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.borders.primary;
}};
`;
const StyledButton = styled(Button)`
  &:hover {
    color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.text.secondary;
}};
    &:before {
      background-color: transparent;
    }
  }
`;
const TopicSummaryRevenueCard = _ref5 => {
  let {
    revenue,
    footprints,
    ...rest
  } = _ref5;
  const {
    t
  } = useTranslation();
  const opportunities = Array.isArray(revenue?.opportunities) ? revenue?.opportunities : [];
  const activityLimit = 3;
  const [expandedProspective, setExpandedProspective] = useState(false);
  const [expandedExisting, setExpandedExisting] = useState(false);
  const [expandedRecentActivity, setExpandedRecentActivity] = useState(false);
  const [filterProspective, setFilterProspective] = useState("All");
  const [opportunityData, setOpportunityData] = useState(undefined);
  const prospectiveOpportunities = opportunities?.filter(opp => (opp.normalizedStage ?? opp.stage) !== "Closed Won") || [];
  const stages = prospectiveOpportunities.map(opp => opp.normalizedStage ?? opp.stage);
  const hasClosedLost = stages.includes("Closed Lost");
  const hasProgressStages = stages.some(stage => stage !== "Closed Lost");
  const prospectiveFilters = [];
  if (prospectiveOpportunities.length > 0) {
    prospectiveFilters.push(t("TopicSummaryRevenueCard.all"));
  }
  if (hasProgressStages) {
    prospectiveFilters.push(t("TopicSummaryRevenueCard.inProgress"));
  }
  if (hasClosedLost) {
    prospectiveFilters.push(t("TopicSummaryRevenueCard.closedLost"));
  }
  const filteredProspective = filterProspective === t("TopicSummaryRevenueCard.all") ? prospectiveOpportunities : filterProspective === t("TopicSummaryRevenueCard.inProgress") ? prospectiveOpportunities.filter(opp => (opp.normalizedStage ?? opp.stage) !== t("TopicSummaryRevenueCard.closedLost")) : prospectiveOpportunities.filter(opp => (opp.normalizedStage ?? opp.stage) === filterProspective);
  const displayedProspectiveOpportunities = expandedProspective ? filteredProspective : filteredProspective.slice(0, activityLimit);
  const totalAmountPerspective = helpers.formatMoneyAmount(filteredProspective.reduce((sum, opp) => {
    return sum + (opp.amount ?? 0);
  }, 0));
  const existingOpportunities = opportunities?.filter(opp => (opp.normalizedStage ?? opp.stage) === "Closed Won") || [];
  const displayedExistingOpportunities = expandedExisting ? existingOpportunities : existingOpportunities.slice(0, activityLimit);
  const totalAmountExisting = helpers.formatMoneyAmount(existingOpportunities.reduce((sum, opp) => {
    return sum + (opp.amount ?? 0);
  }, 0));
  function handleOpenModal(opportunity) {
    setOpportunityData(opportunity);
  }
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(StyledBox, {
    s: {
      p: 4,
      pr: 2,
      mb: 6
    },
    rounded: true
  }, /*#__PURE__*/React.createElement(Box, {
    s: {
      pr: 3
    },
    fullWidth: true,
    justify: "space-between",
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    display: "inline",
    element: "h5",
    fontSize: 4
  }, t("TopicSummaryRevenueCard.prospectiveRevenue")), /*#__PURE__*/React.createElement(Text, {
    color: "primaryGrey",
    display: "inline",
    fontSize: 4,
    weight: "semiBold"
  }, revenue?.opportunities[0].currency, totalAmountPerspective.toLocaleString())), /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 6,
    s: {
      mt: 2
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, !!prospectiveFilters?.length && prospectiveFilters.map(filter => /*#__PURE__*/React.createElement(Button, {
    key: filter,
    color: filter === filterProspective ? "primary" : "primaryGrey",
    onClick: () => setFilterProspective(filter),
    s: {
      px: 3,
      py: 1,
      mb: 2,
      mr: 2
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false
  }, filter)))), !!displayedProspectiveOpportunities.length && /*#__PURE__*/React.createElement(ScrollableTable, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 0,
    fullWidth: true,
    s: {
      mt: 4
    }
  }, /*#__PURE__*/React.createElement(GridRow, {
    cols: 4,
    template: "2fr 2fr 1fr 1.5fr",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.opportunity"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.account"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.amount"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.stage")))), displayedProspectiveOpportunities.map(opportunity => /*#__PURE__*/React.createElement(GridRowLink, {
    color: "text",
    onClick: () => handleOpenModal(opportunity)
  }, /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.name}</div>`
  }, opportunity?.name)), /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.account?.name}</div>`
  }, opportunity?.account?.name)), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "medium"
  }, opportunity?.amount > 0 ? `${opportunity?.currency}${helpers.formatMoneyAmount(opportunity.amount).toLocaleString()}` : "-")), /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.stage}</div>`
  }, opportunity?.stage)))))), filteredProspective.length > activityLimit && /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      setExpandedProspective(!expandedProspective);
    },
    color: "translucent",
    display: "block",
    fontSize: 3.5,
    s: {
      py: 2,
      px: 4,
      mt: 3
    },
    outline: false,
    textAlign: "center",
    fullWidth: true
  }, expandedProspective ? t("TopicSummaryRevenueCard.showLess") : t("TopicSummaryRevenueCard.showAll", {
    count: filteredProspective.length - activityLimit
  }))), /*#__PURE__*/React.createElement(StyledBox, {
    s: {
      p: 4,
      pr: 2,
      mb: 6
    },
    rounded: true
  }, /*#__PURE__*/React.createElement(Box, {
    s: {
      pr: 3
    },
    fullWidth: true,
    justify: "space-between",
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    display: "inline",
    element: "h5",
    fontSize: 4
  }, t("TopicSummaryRevenueCard.existingRevenue")), /*#__PURE__*/React.createElement(Text, {
    color: "primaryGrey",
    display: "inline",
    fontSize: 4,
    weight: "semiBold"
  }, revenue?.opportunities[0].currency, totalAmountExisting.toLocaleString())), !!displayedExistingOpportunities.length && /*#__PURE__*/React.createElement(ScrollableTable, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 0,
    fullWidth: true,
    s: {
      mt: 4
    }
  }, /*#__PURE__*/React.createElement(GridRowExisting, {
    cols: 4,
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.closedWonOpps"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.account"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.amount"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3
  }, t("TopicSummaryRevenueCard.TCV")))), displayedExistingOpportunities.map(opportunity => /*#__PURE__*/React.createElement(GridRowLinkExisting, {
    color: "text",
    onClick: () => handleOpenModal(opportunity)
  }, /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.name}</div>`
  }, opportunity?.name)), /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.account?.name}</div>`
  }, opportunity?.account?.name)), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "medium"
  }, opportunity?.amount > 0 ? `${opportunity?.currency}${helpers.formatMoneyAmount(opportunity.amount).toLocaleString()}` : "-")), /*#__PURE__*/React.createElement(StyledCell, null, /*#__PURE__*/React.createElement(ConcatText, {
    weight: "medium",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<div>${opportunity?.stage}</div>`
  }, opportunity?.tcv ? `${opportunity?.currency}${helpers.formatMoneyAmount(opportunity.tcv).toLocaleString()}` : "-")))))), existingOpportunities.length > activityLimit && /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      setExpandedExisting(!expandedExisting);
    },
    color: "translucent",
    display: "block",
    fontSize: 3.5,
    s: {
      py: 2,
      px: 4,
      mt: 3
    },
    outline: false,
    textAlign: "center",
    fullWidth: true
  }, expandedExisting ? t("TopicSummaryRevenueCard.showLess") : t("TopicSummaryRevenueCard.showAll", {
    count: existingOpportunities.length - activityLimit
  }))), /*#__PURE__*/React.createElement(StyledButton, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      rotate: expandedRecentActivity ? 180 : 90
    }),
    onClick: e => {
      e.stopPropagation();
      setExpandedRecentActivity(!expandedRecentActivity);
    },
    color: "transparent",
    display: "block",
    fontSize: 4,
    s: {
      mt: 0,
      pl: 0
    },
    outline: false,
    textAlign: "left",
    fullWidth: true
  }, t("TopicSummaryRevenueCard.recentActivity")), expandedRecentActivity && /*#__PURE__*/React.createElement(Scrollable, null, /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 1,
    template: "1fr",
    gap: 3,
    fullWidth: true,
    s: {
      ml: -2,
      mt: 2,
      pt: 2
    }
  }, footprints.map(footprint => /*#__PURE__*/React.createElement(TopicFootprintSmall, {
    key: footprint.id,
    footprint: footprint
  })))), /*#__PURE__*/React.createElement(Modal, {
    open: !!opportunityData
  }, /*#__PURE__*/React.createElement(TopicSummaryRevenueCardModal, {
    opportunity: opportunityData,
    onClose: () => setOpportunityData(undefined)
  })));
};
export default TopicSummaryRevenueCard;